import styled from "styled-components";
import {
  Input,
  Select,
  Button,
  Table,
  Tag,
  Modal,
  Checkbox,
  Pagination,
} from "antd";
import { InfoOutlined } from "@ant-design/icons";

// ============================================
// THEME CONSTANTS
// ============================================
export const StyledColors = {
  primary: "#3b82f6",
  primaryHover: "#2563eb",
  tableHeader: "#164ECF",

  success: "#10b981",
  successHover: "#059669",

  danger: "#ef4444",

  grayLight: "#f9fafb",
  grayBorder: "#e5e7eb",
  grayBackground: "#f5f5f5",

  textDark: "#374151",
  textMedium: "#3d4b5c",
  textLight: "#6b7280",

  white: "#fff",
  borderLight: "#e6e6e6",
  backgroundWhite: "#fff",
};

// ============================================
// LAYOUT
// ============================================
export const StyledContainer = styled.div`
  padding: 0;
`;

export const StyledHeader = styled.div`
  margin-bottom: 10px;
  min-width: 0;
  flex: 1;

  h2 {
    color: ${StyledColors.textDark};
    font-weight: 600;
    margin: 0;
    font-size: 24px;
    white-space: normal;
    word-break: break-word;

    @media (max-width: 768px) {
      font-size: 24px;
    }
  }

  span {
    color: ${StyledColors.textDark};
    font-weight: 400;
    font-size: 14px;

    @media (max-width: 768px) {
      font-size: 12px;
    }
  }
`;

export const StyledHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: nowrap;
  align-items: flex-start;

  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

export const StyledContentWrapper = styled.div`
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 16px;
  }

  h4 {
    margin-bottom: 20px;
    color: ${StyledColors.textDark};
  }
`;

export const StyledChatbotWrapper = styled.div`
  display: flex;
  gap: 0;
  width: 100%;
  .task-details-content {
    padding-right: 10px;
    flex: 1;
    min-width: 0;
  }
  .idea-chatbot-area {
    width: 400px;
    min-width: 400px;
    max-width: 400px;
    min-height: 600px;
    height: calc(100vh - 200px);
    border-left: 1px solid #e8e8e8;
    padding: 5px 10px;
    background: white;
    flex-shrink: 0;
    position: sticky;
    top: 70px;
    border-radius: 10px;
    margin-left: 5px;
  }
`;

// ============================================
// FILTERS
// ============================================
export const StyledFiltersContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

export const StyledSearchInput = styled(Input)`
  width: 250px;

  @media (max-width: 768px) {
    width: 100%;
  }

  &.ant-input-affix-wrapper {
    background-color: ${StyledColors.grayLight};
    border: 1px solid ${StyledColors.grayBorder};
  }
`;

export const StyledFilterSelect = styled(Select)`
  width: 180px;

  @media (max-width: 768px) {
    width: 48%;
    min-width: 150px;
  }

  @media (max-width: 480px) {
    width: 100%;
  }

  .ant-select-selector {
    background-color: ${StyledColors.grayLight} !important;
    border: 1px solid ${StyledColors.grayBorder} !important;
  }
`;

// ============================================
// BUTTONS
// ============================================
export const StyledAddButton = styled(Button)`
  background-color: ${StyledColors.primary};
  border-color: ${StyledColors.primary};
  font-weight: 500;
  margin-left: auto;

  @media (max-width: 768px) {
    width: 100%;
  }

  &:hover {
    background-color: ${StyledColors.primaryHover} !important;
    border-color: ${StyledColors.primaryHover} !important;
  }
`;

export const StyledCopyButton = styled(Button)`
  background-color: #e6f4fb;
  border: none;
  color: #4880ff;
  font-weight: 500;
  margin-left: auto;

  @media (max-width: 768px) {
    width: 100%;
  }

  &:hover {
    background-color: #b8e3f9 !important;
    color: #4880ff !important;
  }
`;

export const StyledEditScondaryButton = styled(Button)`
  background: #0f8d37;
  border-color: #0f8d37;
  font-weight: 500;

  @media (max-width: 768px) {
    width: 100%;
  }

  &:hover {
    background: #0c742e !important;
    border-color: #0c742e !important;
  }
`;

export const StyledEditButton = styled(Button)`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #0f8d37;
  color: #fff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #0c742e !important;
  }
`;

export const StyledDeleteButton = styled(Button)`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  color: #fff !important;
  background: #d9363e;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #b92d34 !important;
  }
`;

export const StyledAddModuleButton = styled(Button)`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #d8bd0b;
  color: #fff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #af9a10 !important;
  }
`;

export const StyledDownloadButton = styled(Button)`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #1677ff;
  color: #fff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #125fcc !important;
  }
`;

export const StyledPaymentButton = styled(Button)`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #0b67e6;
  color: #fff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #0952ba !important;
  }
`;

// ============================================
// TABLE
// ============================================
export const StyledTable = styled(Table)`
  .ant-table {
    border: 1px solid #d5d5e4ff;
    font-weight: 500;
  }

  .ant-table-wrapper {
    overflow: auto;
  }

  @media (max-width: 768px) {
    .ant-table {
      font-size: 13px;
    }

    .ant-table-thead > tr > th {
      white-space: nowrap;
      font-size: 12px;
    }

    .ant-table-tbody > tr > td {
      font-size: 12px;
      padding: 6px !important;
    }
  }

  .ant-table-tbody > tr:nth-child(odd) > td {
    background: #f7fbfd;
  }

  .ant-table-tbody > tr:hover > td {
    background: #e6f7ff;
  }

  .ant-table-thead > tr > th {
    background-color: ${StyledColors.tableHeader};
    color: white;
    border-bottom: none;
    padding: 12px 12px !important;
    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    padding: 9px 9px !important;
    border-bottom: 1px solid #d5d5e4ff;
  }

  .ant-table-tbody > tr {
    cursor: pointer;
  }
`;

// ============================================
// STATUS TAG
// ============================================
export const StyledStatusTag = styled(Tag)`
  background-color: ${({ $status }) =>
    $status === "Active" ? StyledColors.success : StyledColors.danger};

  color: white;
  border: none;
  padding: 4px 12px;
  border-radius: 12px;
  font-weight: 500;
`;

/**
 * StyledCommonStatusTag - Reusable status tag component
 * Uses status prop to automatically apply correct styling
 *
 * @param {string} status - Status value (in_progress, pending, completed, etc.)
 *
 * Usage:
 * <StyledCommonStatusTag status="in_progress">In Progress</StyledCommonStatusTag>
 * <StyledCommonStatusTag status="completed">Completed</StyledCommonStatusTag>
 */
export const StyledCommonStatusTag = styled(Tag)`
  border-radius: 20px !important;
  padding: 4px 16px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #fff !important;
  border: none !important;
  line-height: 1.5 !important;
  display: inline-flex !important;
  align-items: center !important;
  width: fit-content !important;
  /* Status-based background colors */
  background: ${({ status }) => {
    const statusLower = status?.toLowerCase().replace(/\s+/g, "-") || "";

    switch (statusLower) {
      case "in-progress":
      case "in_progress":
        return "#4f88ff";
      case "pending":
        return "#f29a2e";
      case "completed":
      case "active":
        return "#18c47c";
      case "draft":
      case "inactive":
        return "#e53935";
      case "delayed":
        return "#e53935";
      case "submitted":
        return "#4f88ff";
      case "approved":
        return "#18c47c";
      case "update_required":
        return "#dd6b20";
      case "open":
        return "#18c47c";
      case "closed":
        return "#e53935";
      case "weekly":
        return "#4f88ff";
      case "fortnightly":
        return "#7c3aed";
      case "monthly":
        return "#0891b2";
      case "quarterly":
        return "#d97706";
      case "yearly":
        return "#059669";
      default:
        return "#9e9e9e";
    }
  }} !important;

  border-color: ${({ status }) => {
    const statusLower = status?.toLowerCase().replace(/\s+/g, "-") || "";

    switch (statusLower) {
      case "in-progress":
      case "in_progress":
        return "#4f88ff";
      case "pending":
        return "#f29a2e";
      case "completed":
      case "active":
        return "#18c47c";
      case "draft":
      case "inactive":
        return "#e53935";
      case "delayed":
        return "#e53935";
      case "submitted":
        return "#4f88ff";
      case "approved":
        return "#18c47c";
      case "update_required":
        return "#dd6b20";
      case "open":
        return "#18c47c";
      case "closed":
        return "#e53935";
      case "weekly":
        return "#4f88ff";
      case "fortnightly":
        return "#7c3aed";
      case "monthly":
        return "#0891b2";
      case "quarterly":
        return "#d97706";
      case "yearly":
        return "#059669";
      default:
        return "#9e9e9e";
    }
  }} !important;
`;

export const StyledCheckboxGroup = styled(Checkbox.Group)`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .ant-checkbox-wrapper {
    margin-inline-start: 0;
    background-color: #e3ffed;
    padding: 10px;
  }

  .ant-checkbox-wrapper .ant-checkbox .ant-checkbox-inner {
    border-color: gray;
  }

  .ant-checkbox-wrapper .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #8decae;
    border-color: #8decae;
  }

  .ant-checkbox-wrapper:hover .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #8decae !important;
    border-color: #8decae !important;
  }
`;

// ============================================
// STRUCTURE COMPONENTS
// ============================================
export const StyledDividerLine = styled.div`
  width: 100%;
  height: 1px;
  background: #e5e7eb;
  margin: 16px 0;
`;

export const StyledEmptyWrapper = styled.div`
  text-align: center;
  padding: 25px;

  @media (max-width: 768px) {
    padding: 40px 16px;
  }
`;

export const StyledSectionWrapper = styled.div`
  background: ${StyledColors.backgroundWhite};
  padding: 22px;
  border-radius: 8px;
  margin-bottom: ${(props) => (props.$noMargin ? "0" : "18px")};

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const StyledSectionTitle = styled.h3`
  font-weight: 600;
  font-size: 18px;
  margin-bottom: 12px;
  display: flex;
  .section-title-text {
    flex: 1;
  }
`;

// export const StyledSectionTitle = styled.h3`
//   margin-bottom: 12px;
//   font-weight: 600;
//   display: flex;
//   justify-content: space-between;
//   align-items: center;

//   .section-title-text {
//     flex: 1;
//   }
// `;

export const StyledFilterRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

// ============================================
// MODAL
// ============================================
export const StyledBaseModal = styled(Modal)`
  .ant-modal-body {
    max-height: calc(100vh - 400px);
    overflow-y: auto;
    overflow-x: hidden !important;
  }

  @media (max-width: 768px) {
    .ant-modal-body {
      max-height: calc(100vh - 240px);
      padding: 12px;
    }
  }
`;

export const StyledModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${(props) => props.$padding || "8px 0 12px 0"};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

export const StyledModalDivider = styled.div`
  height: 1px;
  background: ${StyledColors.borderLight};
  margin-bottom: ${(props) => props.$marginBottom || "18px"};
  margin: ${(props) => props.$margin};

  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
`;

export const StyledModalContent = styled.div`
  padding: ${(props) => props.$padding || "0 24px 24px 24px"};

  @media (max-width: 768px) {
    padding: ${(props) => props.$padding || "0 16px 16px 16px"};
  }
`;

export const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

export const FlexOne = styled.div`
  flex: 1;
`;

// Risk Category Icons
export const StyledRiskIcon = styled(InfoOutlined)`
  margin-right: 8px;
  font-size: 8px;
  color: white !important;

  background-color: ${({ $risk }) => {
    switch ($risk?.toUpperCase()) {
      case "HIGH":
        return "#ff4d4f";
      case "MEDIUM":
        return "#faad14";
      case "LOW":
        return "#52c41a";
      default:
        return "#d9d9d9";
    }
  }};
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  cursor: pointer;
  display: inline-flex;
`;

export const StyledPagination = styled(Pagination)`
  text-align: right;
`;

// Truncated text span for table cells — used with Tooltip for hover preview
export const StyledTruncatedText = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

// Truncated title text inside flex row cells (with expand icons or prefixes)
export const StyledTitleText = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
